// 일정 상세의 "참가자" 섹션 — 성별/팀별 칩 그룹 + 가용시간 시트 호스트.
//
// 일정이 시작된 후에는 collapsible(접힘)이 되고, 시작 전에는 운영진이
// 조퇴/늦참을 바로 보고 다룰 수 있게 항상 펼쳐져 있다. 칩 탭 동작은
// (가용시간 편집 가능 여부 × 매치 존재 여부)에 따라 4분기로 나뉜다.

import { Fragment } from "preact";
import { useState } from "preact/hooks";
import { canSeeNtrp } from "../auth";
import { matchPlayerName } from "../matches";
import { state } from "../state";
import { Icon } from "../ui/components/Icon";
import { formatNtrp, isoMinuteDiff } from "../ui/format";
import { mountPreact } from "../ui/preact-mount";
import { closeSheet, openSheet } from "../ui/sheet";
import { buildParticipantScheduleView } from "./participantSchedule";

interface ParticipantLike {
  event_participant_id: string;
  gender_snapshot?: string;
  ntrp_snapshot?: number | string | null;
  participant_type?: string;
  available_from_at?: string | null;
  available_until_at?: string | null;
}

export function hasCustomAvailabilityWindow(p: ParticipantLike, ev: any): boolean {
  if (!ev) return false;
  const from = p.available_from_at;
  const until = p.available_until_at;
  if (from && ev.court_booking_start_at && isoMinuteDiff(from, ev.court_booking_start_at) !== 0) return true;
  if (until && ev.court_booking_end_at && isoMinuteDiff(until, ev.court_booking_end_at) !== 0) return true;
  return false;
}

export function availabilityWindowLabel(p: ParticipantLike, ev: any): string {
  if (!hasCustomAvailabilityWindow(p, ev)) return "처음부터 끝까지";
  const parts: string[] = [];
  const fromDiff = isoMinuteDiff(p.available_from_at, ev.court_booking_start_at);
  const untilDiff = isoMinuteDiff(ev.court_booking_end_at, p.available_until_at);
  if (fromDiff > 0) parts.push(`${fromDiff}분 늦참`);
  if (untilDiff > 0) parts.push(`${untilDiff}분 조퇴`);
  if (parts.length === 0) return "처음부터 끝까지";
  return parts.join(" · ");
}

export interface ParticipantsSectionParams {
  ev: any;
  participants: ParticipantLike[];
  teams: any[];
  matches: any[];
  roundMap: Record<number, any>;
  canManage: boolean;
  openAvailability: (p: ParticipantLike) => void;
  openGuestLink: () => void;
}

function ParticipantChips({
  group,
  label,
  ev,
  hasMatches,
  canEditAvailability,
  onTap,
}: {
  group: ParticipantLike[];
  label: string;
  ev: any;
  hasMatches: boolean;
  canEditAvailability: boolean;
  onTap: (p: ParticipantLike) => void;
}) {
  if (group.length === 0) return null;
  const tappable = hasMatches || canEditAvailability;
  return (
    <div>
      <div class="gender-label">{label} {group.length}명</div>
      <div class="chip-group">
        {group.map((p) => {
          const name = matchPlayerName(p);
          const meta = canSeeNtrp() ? formatNtrp(p.ntrp_snapshot) : null;
          const customWindow = hasCustomAvailabilityWindow(p, ev);
          return (
            <button
              key={p.event_participant_id}
              type="button"
              class={"chip" + (tappable ? " chip--tap" : "") + (customWindow ? " chip--windowed" : "")}
              disabled={!tappable}
              title={customWindow ? availabilityWindowLabel(p, ev) : undefined}
              onClick={tappable ? () => onTap(p) : undefined}
            >
              {customWindow && <span class="chip__badge" aria-label="시간 조정">⏱</span>}
              <span class="chip__name">{name}</span>
              {meta && <span class="chip__meta">{meta}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ParticipantsSection(params: ParticipantsSectionParams) {
  const { ev, participants, teams, matches, roundMap,
    canManage, openAvailability, openGuestLink } = params;

  const hasMatches = matches.length > 0;
  const beforeStart = ev.status === "draft" || ev.status === "ready";
  const collapsible = !beforeStart && (hasMatches || teams.length > 0);
  const canEditAvailability = canManage && beforeStart;

  const [open, setOpen] = useState(!collapsible);

  function openParticipantScheduleSheet(p: ParticipantLike) {
    const body = buildParticipantScheduleView(p, matches, roundMap);
    if (body) openSheet(`${matchPlayerName(p)} 스케줄`, body);
  }

  function openParticipantCombinedSheet(p: ParticipantLike) {
    const body = document.createElement("div");
    body.className = "stack";
    body.style.gap = "16px";
    if (canEditAvailability) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn btn--ghost btn--block";
      btn.textContent = "조퇴 / 늦참 설정";
      btn.onclick = () => { closeSheet(); openAvailability(p); };
      body.append(btn);
    }
    if (hasMatches) {
      if (canEditAvailability) {
        const hr = document.createElement("hr");
        hr.style.cssText = "border:none;border-top:1px solid var(--border);margin:4px 0";
        body.append(hr);
      }
      const scheduleContent = buildParticipantScheduleView(p, matches, roundMap);
      if (scheduleContent) body.append(scheduleContent);
    }
    openSheet(matchPlayerName(p), body);
  }

  function onChipTap(p: ParticipantLike) {
    if (hasMatches && canEditAvailability) openParticipantCombinedSheet(p);
    else if (canEditAvailability) openAvailability(p);
    else if (hasMatches) openParticipantScheduleSheet(p);
  }

  const titleClass = "section__title" + (collapsible ? " section__title--toggle" : "");
  const onTitleClick = collapsible ? () => setOpen((o) => !o) : undefined;

  let bodyContent: preact.JSX.Element | null = null;
  if (participants.length === 0) {
    const theirLabel = ev.team_b_name || ev.opponent_club_name || "상대 클럽";
    bodyContent = (
      <Fragment>
        <div class="card empty">아직 참가자가 없어요</div>
        {ev.is_friendly_match && canManage && beforeStart && (
          <Fragment>
            <button
              type="button"
              class="btn btn--ghost btn--sm"
              style="margin-top:8px"
              onClick={() => openGuestLink()}
            >
              {theirLabel}에 명단 요청하기
            </button>
            <p class="field__hint">호스트가 직접 추가하려면 참가자에서 게스트로 등록하세요</p>
          </Fragment>
        )}
      </Fragment>
    );
  } else if (ev.is_friendly_match) {
    const ours = participants.filter((p) => p.participant_type !== "guest");
    const theirs = participants.filter((p) => p.participant_type === "guest");
    const ourLabel = ev.team_a_name || state.club?.short_name || "우리 클럽";
    const theirLabel = ev.team_b_name || ev.opponent_club_name || "상대 클럽";
    bodyContent = (
      <Fragment>
        <ParticipantChips
          group={ours}
          label={ourLabel}
          ev={ev}
          hasMatches={hasMatches}
          canEditAvailability={canEditAvailability}
          onTap={onChipTap}
        />
        <ParticipantChips
          group={theirs}
          label={theirLabel}
          ev={ev}
          hasMatches={hasMatches}
          canEditAvailability={canEditAvailability}
          onTap={onChipTap}
        />
        {canManage && beforeStart && (
          <Fragment>
            <button
              type="button"
              class="btn btn--ghost btn--sm"
              style="margin-top:8px"
              onClick={() => openGuestLink()}
            >
              {theirLabel}에 명단 요청하기
            </button>
            <p class="field__hint">호스트가 직접 추가하려면 참가자에서 게스트로 등록하세요</p>
          </Fragment>
        )}
      </Fragment>
    );
  } else {
    const males = participants.filter((p) => p.gender_snapshot === "male");
    const females = participants.filter((p) => p.gender_snapshot === "female");
    const others = participants.filter((p) =>
      p.gender_snapshot !== "male" && p.gender_snapshot !== "female",
    );
    bodyContent = (
      <Fragment>
        <ParticipantChips
          group={males}
          label="남"
          ev={ev}
          hasMatches={hasMatches}
          canEditAvailability={canEditAvailability}
          onTap={onChipTap}
        />
        <ParticipantChips
          group={females}
          label="여"
          ev={ev}
          hasMatches={hasMatches}
          canEditAvailability={canEditAvailability}
          onTap={onChipTap}
        />
        <ParticipantChips
          group={others}
          label="기타"
          ev={ev}
          hasMatches={hasMatches}
          canEditAvailability={canEditAvailability}
          onTap={onChipTap}
        />
      </Fragment>
    );
  }

  return (
    <div class="section">
      <div class={titleClass} onClick={onTitleClick}>
        <h3>참가자</h3>
        <span class="count">{participants.length}명</span>
        {collapsible && (
          <span class={"section__chevron" + (open ? " section__chevron--open" : "")}>
            <Icon name="chevron" size={14} />
          </span>
        )}
      </div>
      <div hidden={!open}>{bodyContent}</div>
    </div>
  );
}

export function buildParticipantsSection(params: ParticipantsSectionParams): HTMLElement {
  return mountPreact(<ParticipantsSection {...params} />);
}
